import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { brand } from '../../config/brand';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { useFormErrors } from './useFormErrors';

export function RegisterPage() {
  const errors = useFormErrors();

  const [form, setForm] = useState({ email: '', username: '', password: '', display_name: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  function update(key: keyof typeof form) {
    return (event: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: event.target.value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    errors.clear();
    setBusy(true);

    try {
      await api.post('/auth/register', form, { anonymous: true });
      setDone(true);
    } catch (error) {
      errors.capture(error);
    } finally {
      setBusy(false);
    }
  }

  /*
   * The same screen whether or not the address was already registered.
   *
   * The server returns an identical response for both (see RegisterAction), and
   * the client has to preserve that - branching here on anything would put the
   * enumeration oracle back after the API went to some trouble to remove it.
   *
   * It also means registration does not sign anybody in. They confirm the
   * address, then log in.
   */
  if (done) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`We have sent a confirmation link to ${form.email}.`}
        footer={
          <>
            Already confirmed? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
          </>
        }
      >
        <Alert tone="info">
          The link is good for 24 hours. If it does not arrive within a few minutes, check your spam folder.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${brand.name}`}
      subtitle="Upload your tracks and find people making things you like."
      footer={
        <>
          Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        <Field
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          required
          value={form.email}
          onChange={update('email')}
          error={errors.fields.email}
        />

        <Field
          label="Username"
          name="username"
          autoComplete="username"
          required
          value={form.username}
          onChange={update('username')}
          hint="Letters, numbers, underscore or hyphen. This is your profile address."
          error={errors.fields.username}
        />

        <Field
          label="Display name"
          name="display_name"
          autoComplete="nickname"
          value={form.display_name}
          onChange={update('display_name')}
          hint="Optional. Defaults to your username."
          error={errors.fields.display_name}
        />

        <Field
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={update('password')}
          // Length is the only rule the server enforces, so it is the only rule
          // stated here. Hinting at symbols people do not need is how you get
          // Password1! instead of a passphrase.
          hint="At least 10 characters. A short phrase beats a complicated word."
          error={errors.fields.password}
        />

        <Button type="submit" loading={busy} full>
          {copy['cta.signup']}
        </Button>
      </form>
    </AuthLayout>
  );
}
